import { AccordionDetails, AccordionSummary, Box, Card, CardContent, css, Tooltip, Typography } from '@mui/material'
import { get, groupBy, isNull } from 'lodash'
import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react'
import {
  Feature_Base_Bool_Exp,
  Feature_Config,
  Feature_Sorting_Config_Bool_Exp,
  Feature_Type_Config,
  Feature_Value,
  Kap_Measure,
  Project_Base,
} from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import {
  isFeatureSelectionInvalid,
  isFeatureTypeWithInvalidSelections,
} from 'src/screens/shared/feature/utils/FeatureValidationUtils'
import { EditButton } from 'src/shared/button/Buttons'
import { CheckIcon, ErrorOutlineIcon, ExpandMoreIcon, InfoIcon } from 'src/shared/icons/Icons'
import { DefaultSectionTypography } from 'src/shared/presentation/DefaultSectionTypography'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Section } from 'src/shared/presentation/Section'
import { S } from 'src/shared/styled/S'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useUserLocale } from 'src/user/UserContext'
import styled from 'styled-components'
import {
  FeatureData,
  useFeatureConfiguration,
} from 'src/screens/shared/configuration/feature/FeaturesConfigurationUtils'
import { TYPOGRAPHY_MIXIN } from 'src/shared/constants/styling-constants'

const FeaturesContainer = styled(Box)`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`

const FeaturesGroupBox = styled(Box)`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`

const AvailableFeaturesContainer = styled(Box)`
  color: ${({ theme }) => theme.colors.text.disabled};
  margin-left: ${({ theme }) => theme.spacing(1.5)};
  margin-top: ${({ theme }) => theme.spacing(1.5)};
`

const TitleStyled = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`

const TitleStyledGroup = styled(Typography)`
  ${TYPOGRAPHY_MIXIN.subtitle2};
  font-style: normal;
`

const FeatureListItem = styled(Typography)<{ $selected?: boolean; $isInvalid?: boolean }>`
  color: ${({ $isInvalid, theme }) => ($isInvalid ? theme.colors.error.dark : 'inherit')};
  display: grid;
  grid-auto-flow: column;
  grid-gap: ${({ theme }) => theme.spacing(1)};
  align-items: center;
  min-height: 1.75rem;
  width: fit-content;
  ${({ $selected }) =>
    $selected &&
    css`
      &:not(:last-child) {
        margin-bottom: ${({ theme }) => theme.spacing(0.625)};
      }
    `}
`

const InfoIconStyled = styled(InfoIcon)`
  font-size: 1.25rem;
`

interface FeatureBaseProps {
  featureBaseWhere: Feature_Base_Bool_Exp
  featureSortingConfigWhere: Feature_Sorting_Config_Bool_Exp
  onEditFeature: (featureTypeId: number) => void
  canEdit: boolean
  kpiSection?: ReactNode
  relatedEntity?: Kap_Measure
}

export const FeatureBase = ({
  featureBaseWhere,
  featureSortingConfigWhere,
  onEditFeature,
  canEdit,
  kpiSection,
  relatedEntity,
}: FeatureBaseProps): ReactElement => {
  const language = useUserLocale()
  const notificationService = useNotificationService()
  const { getMessage } = useMessageSource()
  const { fetchAllFeatureConfigData } = useFeatureConfiguration()

  const [featuresData, setFeaturesData] = useState<FeatureData | undefined>()

  useEffect(() => {
    fetchAllFeatureConfigData(featureSortingConfigWhere, featureBaseWhere)
      .then((data) => {
        setFeaturesData(data)
      })
      .catch((err) => {
        notificationService.operationFailed()
        setFeaturesData(undefined)
        console.error(err)
      })
  }, [notificationService, featureBaseWhere, featureSortingConfigWhere])

  const feature_data = useMemo(() => {
    return featuresData
  }, [featuresData])

  const featureValidation = relatedEntity && relatedEntity?.factsheet !== null

  const renderFeatureValuesSelected = (value: Feature_Value[], featureType: Feature_Type_Config) => {
    return value
      ?.filter((feature) => feature.feature_config.feature_type_id === featureType.id)
      .map((feature) => {
        return (
          <FeatureListItem
            $selected={true}
            variant="body2"
            key={feature.feature_config_id}
            $isInvalid={
              featureValidation && isFeatureSelectionInvalid(featureType, relatedEntity, feature.feature_config)
            }
          >
            {feature.feature_config.selection_type === 'SELECT' && (
              <>
                <CheckIcon /> {get(feature.feature_config.names, language, '')}
                {feature.feature_config.tooltips &&
                  get(feature.feature_config.tooltips, language, '') !== '' &&
                  get(feature.feature_config.tooltips, language, '') !== null && (
                    <Tooltip placement="right" title={get(feature.feature_config.tooltips, language, '')}>
                      <InfoIconStyled />
                    </Tooltip>
                  )}
              </>
            )}

            {feature.feature_config.selection_type === 'TEXT' && (
              <>
                <CheckIcon />
                {feature.other_description}

                {feature?.feature_config?.names?.[language] && (
                  <span style={{ fontStyle: 'italic', marginLeft: 2, fontSize: '0.8em' }}>
                    ({feature.feature_config.names[language]})
                  </span>
                )}

                {feature.feature_config.tooltips &&
                  get(feature.feature_config.tooltips, language, '') !== '' &&
                  get(feature.feature_config.tooltips, language, '') !== null && (
                    <Tooltip placement="right" title={get(feature.feature_config.tooltips, language, '')}>
                      <InfoIconStyled />
                    </Tooltip>
                  )}
              </>
            )}
          </FeatureListItem>
        )
      })
  }

  const renderAvailableFeatureConfigs = (value: Feature_Config[], featureType: Feature_Type_Config) => {
    return value
      ?.filter((feature) => feature.feature_type_id === featureType.id)
      .map((availableFeature) => {
        return (
          <FeatureListItem variant="body2" key={availableFeature.id} color="inherit">
            {get(availableFeature.names, language, '')}

            {availableFeature.selection_type === 'TEXT' && (
              <span style={{ fontStyle: 'italic', marginLeft: 2, fontSize: '0.8em' }}>
                {getMessage('label.others.features')}
              </span>
            )}

            {availableFeature.tooltips &&
              get(availableFeature.tooltips, language, '') !== '' &&
              get(availableFeature.tooltips, language, '') !== null && (
                <Tooltip placement="right" title={get(availableFeature.tooltips, language, '')}>
                  <InfoIconStyled />
                </Tooltip>
              )}
          </FeatureListItem>
        )
      })
  }

  return (
    <>
      {feature_data &&
        feature_data.featuresTypeConfigs
          .filter(
            (featureType) =>
              feature_data.featureConfigs.find(
                (feature) => feature.feature_type_id === featureType.id && feature.active,
              ) ||
              feature_data.selectedFeatureValues.find(
                (feature) => feature.feature_config.feature_type_id === featureType.id,
              ),
          )
          .map((featureType) => {
            const selectedFeatureValues: Feature_Value[] = feature_data.selectedFeatureValues.filter(
              (feature) => feature.feature_config.feature_type_id === featureType.id,
            )

            const featureTypeHasSelectedFeatureValues = selectedFeatureValues.length > 0

            return (
              <Section
                key={featureType.code}
                id={featureType.code}
                title={get(featureType.names, language, '')}
                actionButton={<EditButton onClick={() => onEditFeature(featureType.id)} hidden={!canEdit} />}
                helpAndInstructions={
                  <HelpAndInstructions
                    labelKey={featureType.help_and_instruction_key}
                    customDescriptionText={featureType.description && get(featureType.description, language, '')}
                  />
                }
              >
                <Card>
                  <CardContent>
                    <FeaturesContainer>
                      <TitleStyled variant="h6" pb={2}>
                        {getMessage('label.your.selection')}
                      </TitleStyled>
                      {featureTypeHasSelectedFeatureValues ? (
                        <>
                          {/* Show feature groups first */}
                          {feature_data.featureGroupConfigs
                            .filter(
                              (fg) =>
                                feature_data.selectedFeatureValuesGroupedByGroupId[fg.id] !== null &&
                                feature_data.selectedFeatureValuesGroupedByGroupId[fg.id] !== undefined &&
                                feature_data.selectedFeatureValuesGroupedByGroupId[fg.id]?.[0]?.feature_config
                                  ?.feature_type_id === featureType.id,
                            )
                            .map((fg) => {
                              const value = feature_data.selectedFeatureValuesGroupedByGroupId[fg.id]

                              return (
                                <FeaturesGroupBox key={fg.id}>
                                  {feature_data.featureGroupConfigs.find(
                                    (group) => group.id == fg.id && !isNull(group.id),
                                  ) && (
                                    <TitleStyledGroup mb={1}>
                                      {feature_data.featureGroupConfigs
                                        .filter((group) => group.id == fg.id)
                                        .map((group) => get(group.names, language, ''))}
                                    </TitleStyledGroup>
                                  )}

                                  <Box sx={{ ml: 2 }}>{renderFeatureValuesSelected(value, featureType)}</Box>
                                </FeaturesGroupBox>
                              )
                            })}

                          {/* Show standalone features last  */}
                          {renderFeatureValuesSelected(
                            feature_data.selectedFeatureValuesGroupedByGroupId['null']?.filter(
                              (fv) => fv.feature_config.feature_type_id === featureType.id,
                            ),
                            featureType,
                          )}
                        </>
                      ) : (
                        <DefaultSectionTypography type="SELECTION" $standAlone={false} />
                      )}
                    </FeaturesContainer>

                    {/* Available Feature Collapsable section */}
                    <S.Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">{getMessage('label.available.features')}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <AvailableFeaturesContainer>
                          {(() => {
                            const activeAvailableFeatureConfigs = feature_data.availableFeatureConfigs.filter((fc) => {
                              if (featureValidation) {
                                return fc.active && !isFeatureSelectionInvalid(featureType, relatedEntity, fc)
                              }

                              return fc.active
                            })

                            const availableFeaturesConfigsGroupedByGroupId = groupBy(
                              activeAvailableFeatureConfigs,
                              (x) => x.feature_group_id,
                            )

                            const availableFeatureConfigsForFeatureType = activeAvailableFeatureConfigs.filter(
                              (availableFeature) => availableFeature.feature_type_id === featureType.id,
                            )

                            if (availableFeatureConfigsForFeatureType.length > 0) {
                              return (
                                <>
                                  {/* Show feature groups first */}
                                  {feature_data.featureGroupConfigs
                                    .filter(
                                      (fg) =>
                                        availableFeaturesConfigsGroupedByGroupId[fg.id] !== null &&
                                        availableFeaturesConfigsGroupedByGroupId[fg.id] !== undefined &&
                                        availableFeaturesConfigsGroupedByGroupId[fg.id][0].feature_type_id ===
                                          featureType.id &&
                                        (featureValidation
                                          ? availableFeaturesConfigsGroupedByGroupId[fg.id].filter(
                                              (v) => !isFeatureSelectionInvalid(featureType, relatedEntity, v),
                                            )
                                          : true),
                                    )
                                    .map((fg) => {
                                      const featureConfigs = availableFeaturesConfigsGroupedByGroupId[fg.id]

                                      return (
                                        featureConfigs.length > 0 && (
                                          <Box key={fg.id} mt={2}>
                                            {feature_data.featureGroupConfigs.find(
                                              (group) => group.id == fg.id && !isNull(group.id),
                                            ) && (
                                              <TitleStyledGroup>
                                                {feature_data.featureGroupConfigs
                                                  .filter((group) => group.id == fg.id)
                                                  .map((group) => get(group.names, language, ''))}
                                              </TitleStyledGroup>
                                            )}

                                            <Box sx={{ ml: 2 }}>
                                              {renderAvailableFeatureConfigs(featureConfigs, featureType)}
                                            </Box>
                                          </Box>
                                        )
                                      )
                                    })}

                                  {/* Show standalone feature configs last */}
                                  {(() => {
                                    const standaloneFeatureConfigs = availableFeaturesConfigsGroupedByGroupId[
                                      'null'
                                    ]?.filter((fc) => fc.feature_type_id === featureType.id)

                                    return renderAvailableFeatureConfigs(standaloneFeatureConfigs, featureType)
                                  })()}
                                </>
                              )
                            } else {
                              return (
                                <FeatureListItem variant="body2" color="inherit">
                                  {getMessage('label.available.features.none')}
                                </FeatureListItem>
                              )
                            }
                          })()}
                        </AvailableFeaturesContainer>
                      </AccordionDetails>
                    </S.Accordion>

                    {featureValidation && isFeatureTypeWithInvalidSelections(featureType, relatedEntity) && (
                      <FeatureListItem variant="body2" $isInvalid sx={{ mt: 4 }}>
                        <ErrorOutlineIcon />
                        {getMessage('validation.deviating.features')}
                      </FeatureListItem>
                    )}
                  </CardContent>
                </Card>
              </Section>
            )
          })}

      {feature_data?.featuresTypeConfigs && feature_data.featuresTypeConfigs.length > 0 && kpiSection}
    </>
  )
}
